import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import Badge from "@/components/base/Badge";
import ShareBar from "@/components/feature/ShareBar";
import { EmptyState, Spinner } from "@/components/base/StateViews";
import { getRelatedReviews, getReviewById, getWorkById } from "@/utils/data";
import { useSeo } from "@/hooks/useSeo";

export default function ReviewDetail() {
  const { id = "" } = useParams();
  const [loading, setLoading] = useState(true);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const review = getReviewById(id);

  useEffect(() => {
    setLoading(true);
    setLiked(false);
    const timer = window.setTimeout(() => setLoading(false), 260);
    return () => window.clearTimeout(timer);
  }, [id]);

  useEffect(() => {
    setLikes(review?.likes ?? 0);
  }, [review]);

  useSeo({
    title: review ? `${review.title} - 影评详情 - 亚洲天堂` : "影评未找到 - 亚洲天堂",
    description: review?.excerpt,
    keywords: review ? [review.workTitle, ...review.tags, "影评详情", "亚洲天堂"].join(",") : undefined,
  });

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-site px-4 py-16 md:px-6">
        <Spinner label="正在加载影评…" />
      </div>
    );
  }

  if (!review) {
    return (
      <div className="mx-auto w-full max-w-site px-4 py-16 md:px-6">
        <EmptyState
          title="影评未找到"
          description="这篇影评可能已被移除，去看看其他影评吧。"
          icon="ri-chat-quote-line"
        />
        <div className="mt-6 flex justify-center">
          <Link to="/category/review" className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap">
            浏览影评专题
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedReviews(review);
  const work = getWorkById(review.workId);

  const handleLike = () => {
    if (liked) {
      setLikes((v) => v - 1);
      setLiked(false);
    } else {
      setLikes((v) => v + 1);
      setLiked(true);
    }
  };

  return (
    <div className="animate-fade-in">
      <article className="mx-auto w-full max-w-site px-4 py-8 md:px-6 md:py-10">
        <div className="mb-4">
          <Breadcrumb items={[{ label: "影评", to: "/category/review" }, { label: review.title.slice(0, 10) + "…" }]} />
        </div>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-3 flex flex-wrap gap-2">
              <Badge tone={review.category === "special" ? "accent" : "primary"}>
                {review.category === "movie" ? "电影影评" : review.category === "tv" ? "电视剧影评" : "专题影评"}
              </Badge>
              {review.tags.map((tag) => (
                <Badge key={tag} tone="secondary">#{tag}</Badge>
              ))}
            </div>

            <h1 className="font-heading text-2xl font-bold leading-snug text-foreground-900 md:text-3xl">
              {review.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-4">
              <span className="flex items-center gap-2">
                <img src={review.avatar} alt={review.author} className="h-9 w-9 rounded-full object-cover object-top" />
                <span className="flex flex-col leading-tight">
                  <span className="text-sm font-medium text-foreground-800">{review.author}</span>
                  <span className="text-xs text-foreground-400">{review.date}</span>
                </span>
              </span>
              <span className="text-sm text-foreground-500">
                关联作品：
                <Link
                  to={`/detail/work/${review.workId}`}
                  className="font-medium text-primary-700 transition-colors hover:text-accent-600"
                >
                  《{review.workTitle}》
                </Link>
              </span>
            </div>

            <div className="mt-5 overflow-hidden rounded-lg bg-background-200">
              <img
                src={review.cover}
                alt={review.title}
                title={`${review.title} - 影评 - 亚洲天堂`}
                className="h-auto w-full object-cover object-top"
              />
            </div>

            <div className="mt-6 flex flex-col gap-4 text-base leading-loose text-foreground-700">
              {review.content.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-background-200 pt-5">
              <button
                type="button"
                onClick={handleLike}
                className={`flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                  liked
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-accent-300 hover:text-accent-600"
                }`}
              >
                <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} aria-hidden="true"></i>
                点赞 {likes}
              </button>
              <ShareBar title={review.title} />
            </div>

            {work ? (
              <div className="mt-8 flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-4">
                <span className="h-20 w-14 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img src={work.poster} alt={work.title} className="h-full w-full object-cover object-top" />
                </span>
                <span className="flex flex-1 flex-col">
                  <span className="text-xs text-foreground-400">影评关联作品</span>
                  <Link
                    to={`/detail/work/${work.id}`}
                    className="text-base font-semibold text-foreground-900 transition-colors hover:text-primary-700"
                  >
                    《{work.title}》
                  </Link>
                  <span className="text-xs text-foreground-500">{work.year} · {work.region} · {work.genres.join(" / ")}</span>
                </span>
                <Link
                  to={`/detail/work/${work.id}`}
                  className="shrink-0 rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 whitespace-nowrap"
                >
                  查看详情
                </Link>
              </div>
            ) : null}
          </div>

          <aside className="lg:col-span-1">
            <h2 className="mb-4 font-heading text-lg font-bold text-foreground-900">相关影评推荐</h2>
            <div className="flex flex-col gap-3">
              {related.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/detail/review/${rel.id}`}
                  className="group rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200"
                >
                  <span className="mb-1.5 flex items-center gap-2 text-xs text-foreground-400">
                    <img src={rel.avatar} alt={rel.author} className="h-5 w-5 rounded-full object-cover object-top" />
                    {rel.author}
                  </span>
                  <span className="line-clamp-2 text-sm font-medium text-foreground-800 transition-colors group-hover:text-primary-700">
                    {rel.title}
                  </span>
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </article>
    </div>
  );
}